/**
 * PNG rasterisation. Browser-only — it needs a canvas.
 *
 * The SVG is handed to the image decoder as a base64 `data:` URL rather than a
 * blob URL: several browsers taint the canvas for SVG loaded from a blob URL,
 * and a tainted canvas makes `toBlob` throw at the very last step. `btoa` alone
 * is not enough either, because it rejects any code point above 255 — so the
 * string is UTF-8 encoded first, which is what keeps accented and CJK labels
 * from breaking the export.
 */
import type { FlowGraph } from '../graph/model/types';
import type { SvgExportOptions } from './svg';
import { exportSvg } from './svg';

export interface PngExportOptions extends SvgExportOptions {
  /** Extra multiplier for a crisp result on high-density screens. */
  readonly pixelRatio: number;
}

/** Browsers refuse canvases beyond roughly this on either axis. */
export const MAX_CANVAS_DIMENSION = 16384;

export function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  // Chunked to stay well under the argument-count limit of `String.fromCharCode`.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Largest scale that keeps both axes inside the canvas limit.
 * Returns the requested scale untouched when it already fits.
 */
export function clampPngScale(
  width: number,
  height: number,
  scale: number,
  pixelRatio: number,
): number {
  const effective = Math.max(0.01, scale * pixelRatio);
  const longest = Math.max(width * effective, height * effective);
  if (longest <= MAX_CANVAS_DIMENSION) return scale;
  return (scale * MAX_CANVAS_DIMENSION) / longest;
}

export async function exportPng(
  graph: FlowGraph,
  options: Partial<PngExportOptions> = {},
): Promise<Blob> {
  if (typeof document === 'undefined') {
    throw new Error('PNG export needs a browser environment.');
  }

  const pixelRatio = options.pixelRatio ?? 2;
  const requestedScale = options.scale ?? 1;

  // Render once at scale 1 to learn the natural size, then re-render at the
  // scale that actually fits. Cheap: it is string building, not rasterising.
  const probe = exportSvg(graph, { ...options, scale: 1 });
  const dimensions = readSvgSize(probe);
  const scale = clampPngScale(dimensions.width, dimensions.height, requestedScale, pixelRatio);

  const svg = exportSvg(graph, { ...options, scale: 1 });
  const width = Math.max(1, Math.round(dimensions.width * scale * pixelRatio));
  const height = Math.max(1, Math.round(dimensions.height * scale * pixelRatio));

  const image = await loadImage(`data:image/svg+xml;base64,${utf8ToBase64(svg)}`);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create a drawing context for the PNG.');

  if (options.background) {
    context.fillStyle = options.background;
    context.fillRect(0, 0, width, height);
  }
  context.drawImage(image, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The browser could not encode the PNG.'));
      }, 'image/png');
    } catch (error) {
      // Thrown when the canvas is tainted, which should not happen with a
      // data: URL but is worth reporting clearly if it ever does.
      reject(
        new Error(
          `The PNG could not be produced: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'sync';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The diagram image could not be decoded.'));
    image.src = src;
  });
}

/** Pulls width/height back out of a serialised SVG without parsing it. */
function readSvgSize(svg: string): { width: number; height: number } {
  const width = /\swidth="(\d+(?:\.\d+)?)"/.exec(svg);
  const height = /\sheight="(\d+(?:\.\d+)?)"/.exec(svg);
  return {
    width: width ? Number(width[1]) : 800,
    height: height ? Number(height[1]) : 600,
  };
}
